import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getLabDataByName, updateLabData } from '../services/labService';
import { getProfile, parseProfileForFormula } from '../services/profileService';
import { getMemberLabData } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';
import CustomAlert from '../Components/CustomAlert';
import { useCustomAlert } from '../hooks/useCustomAlert';
import RbcFormula from '../formula/Rbc';
import HemoglobinFormula from '../formula/Hemoglobin';
import HematocritFormula from '../formula/Hematocrit';
import WbcFormula from '../formula/Wbc';
import PlateletFormula from '../formula/Platelet';
import McvFormula from '../formula/Mcv';
import MchFormula from '../formula/Mch';
import MchcFormula from '../formula/Mchc';
import { STATUS_COLORS, getStatusHex, getWorstStatusColor } from '../utils/healthStatusColors';
import { CBC_FETCH_KEY_GROUPS, CBC_ADDITIONAL_KEY_GROUPS, buildCbcStatusSummary, fetchCbcLabGroupsWithFallbacks, mergeCbcItemsWithMemberLab, mergeAdditionalCbcItems } from '../utils/cbcDataUtils';

// Differential/morphology CBC fields shown as plain informational rows (no color-coded formula),
// matching the parameter set the admin health report shows for this category.
const CBC_EXTRA_DISPLAY_FIELDS = [
  { field: 'rdw', label: 'RDW', unit: ' %' },
  { field: 'mpv', label: 'MPV', unit: ' fL' },
  { field: 'atypicalLymphocyte', label: 'Atypical Lymphocyte', unit: ' %' },
  { field: 'anisocytosis', label: 'Anisocytosis', unit: '' },
  { field: 'bandForm', label: 'Band form', unit: '' },
  { field: 'hypochromic', label: 'Hypochromic', unit: '' },
  { field: 'spherocyte', label: 'Spherocyte', unit: '' },
  { field: 'otherBlood', label: 'Other (Blood)', unit: '' },
  { field: 'myeloblast', label: 'Myeloblast', unit: '' },
  { field: 'promyelocyte', label: 'Promyelocyte', unit: '' },
  { field: 'myelocyte', label: 'Myelocyte', unit: '' },
  { field: 'metamyelocyte', label: 'Metamyelocyte', unit: '' },
  { field: 'lymphoblast', label: 'Lymphoblast', unit: '' },
  { field: 'macrocyte', label: 'Macrocyte', unit: '' },
  { field: 'poikilocytosis', label: 'Poikilocytosis', unit: '' },
  { field: 'ovalocyte', label: 'Ovalocyte', unit: '' },
  { field: 'targetCell', label: 'Target cell', unit: '' },
  { field: 'schistocyte', label: 'Schistocyte', unit: '' },
  { field: 'tearDrop', label: 'Tear drop cell', unit: '' },
  { field: 'burrCell', label: 'Burr cell', unit: '' },
  { field: 'polychromasia', label: 'Polychromasia', unit: '' },
  { field: 'blastCell', label: 'Blast cell', unit: '' },
  { field: 'nrbc', label: 'NRBC', unit: '' },
  { field: 'microcyte', label: 'Microcyte', unit: '' },
];

// CBC_ADDITIONAL_KEY_GROUPS fields handled generically here (rbcMorphology already has its own dedicated flow)
const ADDITIONAL_MORPHOLOGY_FIELDS = Object.keys(CBC_ADDITIONAL_KEY_GROUPS).filter((field) => field !== 'rbcMorphology');

const CBCContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #999;
  font-weight: normal;
  margin-top: 4px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || STATUS_COLORS.green};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CBCIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AbnormalName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 4px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalStatus = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ResultCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 0;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const CardHeader = styled.div`
  background: #00BBD3;
  color: white;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CardBody = styled.div`
  padding: 16px;
`;

const DateRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const DateLabel = styled.div`
  font-size: 14px;
  color: #999;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const DateValue = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const HospitalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const HospitalLabel = styled.div`
  font-size: 14px;
  color: #999;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const HospitalValue = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const TestResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TestResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

const TestLabel = styled.div`
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const TestValue = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const HistorySection = styled.div`
  margin-top: 40px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #00BBD3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: ${STATUS_COLORS.green};
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00BBD3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const CBCPage = () => {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useCustomAlert();
  const [loading, setLoading] = useState(true);
  const [cbcRecords, setCbcRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [latestDate, setLatestDate] = useState('');
  const [statusCardColor, setStatusCardColor] = useState(STATUS_COLORS.gray);
  const [profileData, setProfileData] = useState({ age: 0, gender: '' });
  const [latestValues, setLatestValues] = useState({
    rbc: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    hemoglobin: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    hematocrit: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    wbc: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    platelet: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    plateletCount: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    mcv: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    mch: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    mchc: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    neutrophile: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    lymphocyte: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    monocyte: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    basophil: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    eosinophil: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray }
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    wbc: '',
    rbc: '',
    rbcMorphology: '',
    hemoglobin: '',
    hematocrit: '',
    platelet: '',
    plateletCount: '',
    mcv: '',
    mch: '',
    mchc: '',
    neutrophile: '',
    lymphocyte: '',
    monocyte: '',
    basophil: '',
    eosinophil: ''
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      wbc: record.wbc || '',
      rbc: record.rbc || '',
      rbcMorphology: record.rbcMorphology || '',
      hemoglobin: record.hemoglobin || '',
      hematocrit: record.hematocrit || '',
      platelet: record.platelet || '',
      plateletCount: record.plateletCount || '',
      mcv: record.mcv || '',
      mch: record.mch || '',
      mchc: record.mchc || '',
      neutrophile: record.neutrophile || '',
      lymphocyte: record.lymphocyte || '',
      monocyte: record.monocyte || '',
      basophil: record.basophil || '',
      eosinophil: record.eosinophil || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {

      const updates = [];

      if (editForm.wbc && editForm.wbc !== editingRecord.wbc) {
        updates.push(updateLabData(editingRecord.rawDate, 'wbc', editForm.wbc, 'h'));
      }

      if (editForm.rbc && editForm.rbc !== editingRecord.rbc) {
        updates.push(updateLabData(editingRecord.rawDate, 'rbc', editForm.rbc, 'h'));
      }

      if (editForm.rbcMorphology !== undefined && editForm.rbcMorphology !== (editingRecord.rbcMorphology || '')) {
        updates.push(updateLabData(editingRecord.rawDate, 'rbc_morphology', editForm.rbcMorphology, 'h'));
      }

      if (editForm.hemoglobin && editForm.hemoglobin !== editingRecord.hemoglobin) {
        updates.push(updateLabData(editingRecord.rawDate, 'hb', editForm.hemoglobin, 'h'));
      }

      if (editForm.hematocrit && editForm.hematocrit !== editingRecord.hematocrit) {
        updates.push(updateLabData(editingRecord.rawDate, 'hct', editForm.hematocrit, 'h'));
      }

      if (editForm.platelet && editForm.platelet !== editingRecord.platelet) {
        updates.push(updateLabData(editingRecord.rawDate, 'cbcpltresult', editForm.platelet, 'h'));
      }

      if (editForm.plateletCount && editForm.plateletCount !== editingRecord.plateletCount) {
        updates.push(updateLabData(editingRecord.rawDate, 'plt', editForm.plateletCount, 'h'));
      }

      if (editForm.mcv && editForm.mcv !== editingRecord.mcv) {
        updates.push(updateLabData(editingRecord.rawDate, 'mcv', editForm.mcv, 'h'));
      }

      if (editForm.mch && editForm.mch !== editingRecord.mch) {
        updates.push(updateLabData(editingRecord.rawDate, 'mch', editForm.mch, 'h'));
      }

      if (editForm.mchc && editForm.mchc !== editingRecord.mchc) {
        updates.push(updateLabData(editingRecord.rawDate, 'mchc', editForm.mchc, 'h'));
      }

      if (editForm.neutrophile && editForm.neutrophile !== editingRecord.neutrophile) {
        updates.push(updateLabData(editingRecord.rawDate, 'neutrophile', editForm.neutrophile, 'h'));
      }

      if (editForm.lymphocyte && editForm.lymphocyte !== editingRecord.lymphocyte) {
        updates.push(updateLabData(editingRecord.rawDate, 'lymphocyte-count', editForm.lymphocyte, 'h'));
      }

      if (editForm.monocyte && editForm.monocyte !== editingRecord.monocyte) {
        updates.push(updateLabData(editingRecord.rawDate, 'monocyte-count', editForm.monocyte, 'h'));
      }

      if (editForm.basophil && editForm.basophil !== editingRecord.basophil) {
        updates.push(updateLabData(editingRecord.rawDate, 'basophil-count', editForm.basophil, 'h'));
      }

      if (editForm.eosinophil && editForm.eosinophil !== editingRecord.eosinophil) {
        updates.push(updateLabData(editingRecord.rawDate, 'cbceosinophileresult', editForm.eosinophil, 'h'));
      }

      await Promise.all(updates);

      showAlert('อัปเดตข้อมูลเรียบร้อยแล้ว');

      await loadCBCData();

      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      showAlert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = (date.getFullYear() + 543).toString();
    return `${day}/${month}/${year}`;
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return '';

    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const thaiYear = date.getFullYear() + 543;
    const thaiMonth = thaiMonths[date.getMonth()];

    return `${date.getDate()} ${thaiMonth} ${thaiYear}`;
  };

  const loadCBCData = async () => {
    try {
      setLoading(true);

      const profileResponse = await getProfile();
      const profile = profileResponse?.data || profileResponse?.profile || {};
      const { age, gender } = parseProfileForFormula(profile);

      setProfileData({ age, gender });

      const cbcResponses = await fetchCbcLabGroupsWithFallbacks(getLabDataByName, CBC_FETCH_KEY_GROUPS);

      const memberLabData = await getMemberLabData().catch(() => null);
      const mergedCbcItems = mergeCbcItemsWithMemberLab({
        rbcItems: cbcResponses?.rbc?.data || [],
        hemoglobinItems: cbcResponses?.hemoglobin?.data || [],
        hematocritItems: cbcResponses?.hematocrit?.data || [],
        wbcItems: cbcResponses?.wbc?.data || [],
        plateletItems: cbcResponses?.platelet?.data || [],
        plateletCountItems: cbcResponses?.plateletCount?.data || [],
        mcvItems: cbcResponses?.mcv?.data || [],
        mchItems: cbcResponses?.mch?.data || [],
        mchcItems: cbcResponses?.mchc?.data || [],
        neutrophileItems: cbcResponses?.neutrophile?.data || [],
        lymphocyteItems: cbcResponses?.lymphocyte?.data || [],
        monocyteItems: cbcResponses?.monocyte?.data || [],
        basophilItems: cbcResponses?.basophil?.data || [],
        eosinophilItems: cbcResponses?.eosinophil?.data || [],
        rdwItems: cbcResponses?.rdw?.data || [],
        mpvItems: cbcResponses?.mpv?.data || [],
        rbcMorphologyItems: cbcResponses?.rbcMorphology?.data || [],
        memberLabData,
      });

      const additionalItemsInput = {};
      ADDITIONAL_MORPHOLOGY_FIELDS.forEach((field) => {
        additionalItemsInput[field] = cbcResponses?.[field]?.data || [];
      });
      const mergedAdditionalItems = mergeAdditionalCbcItems(additionalItemsInput, memberLabData);

      let wbcArray = mergedCbcItems.wbcItems || [];
      let rbcArray = mergedCbcItems.rbcItems || [];
      let hemoglobinArray = mergedCbcItems.hemoglobinItems || [];
      let hematocritArray = mergedCbcItems.hematocritItems || [];
      let plateletArray = mergedCbcItems.plateletItems || [];
      let plateletCountArray = mergedCbcItems.plateletCountItems || [];
      let mcvArray = mergedCbcItems.mcvItems || [];
      let mchArray = mergedCbcItems.mchItems || [];
      let mchcArray = mergedCbcItems.mchcItems || [];
      let neutrophileArray = mergedCbcItems.neutrophileItems || [];
      let lymphocyteArray = mergedCbcItems.lymphocyteItems || [];
      let monocyteArray = mergedCbcItems.monocyteItems || [];
      let basophilArray = mergedCbcItems.basophilItems || [];
      let eosinophilArray = mergedCbcItems.eosinophilItems || [];
      let rdwArray = mergedCbcItems.rdwItems || [];
      let mpvArray = mergedCbcItems.mpvItems || [];
      let rbcMorphologyArray = mergedCbcItems.rbcMorphologyItems || [];


      // Combine all records by date
      const recordsMap = new Map();

      wbcArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).wbc = item.value;
      });

      rbcArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).rbc = item.value;
      });

      hemoglobinArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).hemoglobin = item.value;
      });

      hematocritArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).hematocrit = item.value;
      });

      plateletArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).platelet = item.value;
      });

      plateletCountArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).plateletCount = item.value;
      });

      mcvArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).mcv = item.value;
      });

      mchArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).mch = item.value;
      });

      mchcArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).mchc = item.value;
      });

      neutrophileArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).neutrophile = item.value;
      });

      lymphocyteArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).lymphocyte = item.value;
      });

      monocyteArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).monocyte = item.value;
      });

      basophilArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).basophil = item.value;
      });

      eosinophilArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).eosinophil = item.value;
      });

      rbcMorphologyArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).rbcMorphology = item.value;
      });

      rdwArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).rdw = item.value;
      });

      mpvArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).mpv = item.value;
      });

      ADDITIONAL_MORPHOLOGY_FIELDS.forEach((field) => {
        (mergedAdditionalItems[field] || []).forEach(item => {
          const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
          if (!recordsMap.has(date)) {
            recordsMap.set(date, { date, rawDate: item.dateadd, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
          }
          recordsMap.get(date)[field] = item.value;
        });
      });

      const combinedRecords = Array.from(recordsMap.values()).sort((a, b) =>
        new Date(b.date) - new Date(a.date)
      );

      // คำนวณ risk สำหรับทุก record (async formula ต้องทำตรงนี้ ไม่ใช่ใน render)
      const checkRange = (val, min, max) => val < min || val > max;

      for (const record of combinedRecords) {
        const risks = [];
        if (record.wbc) {
          const r = await WbcFormula(parseFloat(record.wbc), age, gender);
          if (r?.color === 'red') risks.push('WBC');
        }
        if (record.rbc) {
          const r = await RbcFormula(parseFloat(record.rbc), age, gender);
          if (r?.color === 'red') risks.push('RBC');
        }
        if (record.hemoglobin) {
          const r = await HemoglobinFormula(parseFloat(record.hemoglobin), age, gender);
          if (r?.color === 'red') risks.push('Hb');
        }
        if (record.hematocrit) {
          const r = await HematocritFormula(parseFloat(record.hematocrit), age, gender);
          if (r?.color === 'red') risks.push('Hct');
        }
        if (record.platelet) {
          const r = await PlateletFormula(parseFloat(record.platelet), age, gender);
          if (r?.color === 'red') risks.push('PLT');
        }
        if (record.plateletCount) {
          const r = await PlateletFormula(parseFloat(record.plateletCount), age, gender);
          if (r?.color === 'red') risks.push('Platelets count');
        }
        if (record.mcv) {
          const r = await McvFormula(parseFloat(record.mcv), age, gender);
          if (r?.color === 'red') risks.push('MCV');
        }
        if (record.mch) {
          const r = await MchFormula(parseFloat(record.mch), age, gender);
          if (r?.color === 'red') risks.push('MCH');
        }
        if (record.mchc) {
          const r = await MchcFormula(parseFloat(record.mchc), age, gender);
          if (r?.color === 'red') risks.push('MCHC');
        }
        if (record.neutrophile && checkRange(parseFloat(record.neutrophile), 46.5, 75)) risks.push('Neutrophile');
        if (record.lymphocyte && checkRange(parseFloat(record.lymphocyte), 12, 44)) risks.push('Lymphocyte');
        if (record.monocyte && checkRange(parseFloat(record.monocyte), 0, 11.2)) risks.push('Monocyte');
        if (record.basophil && checkRange(parseFloat(record.basophil), 0, 2.5)) risks.push('Basophil');
        if (record.eosinophil && checkRange(parseFloat(record.eosinophil), 0, 9.5)) risks.push('Eosinophil');

        record.risks = risks;
        record.riskStatus = risks.length > 0 ? `ผิดปกติ (${risks.join(', ')})` : 'ปกติ';
        record.riskColor = risks.length > 0 ? '#ff4757' : '#34A853';
      }

      // แสดงแค่ 2 การ์ดล่าสุด
      setCbcRecords(combinedRecords);

      // เก็บทั้งหมดไว้สำหรับตาราง
      setAllRecords(combinedRecords);

      // Set latest date
      if (combinedRecords.length > 0) {
        setLatestDate(formatThaiDate(combinedRecords[0].date));

        // Calculate status for latest values using formulas
        const latest = combinedRecords[0];
        const colorMap = STATUS_COLORS;

        const rbcValue = parseFloat(latest.rbc || 0);
        const rbcResult = latest.rbc ? await RbcFormula(rbcValue, age, gender) : null;

        const hemoglobinValue = parseFloat(latest.hemoglobin || 0);
        const hemoglobinResult = latest.hemoglobin ? await HemoglobinFormula(hemoglobinValue, age, gender) : null;

        const hematocritValue = parseFloat(latest.hematocrit || 0);
        const hematocritResult = latest.hematocrit ? await HematocritFormula(hematocritValue, age, gender) : null;

        const wbcValue = parseFloat(latest.wbc || 0);
        const wbcResult = latest.wbc ? await WbcFormula(wbcValue, age, gender) : null;

        const plateletValue = parseFloat(latest.platelet || 0);
        const plateletResult = latest.platelet ? await PlateletFormula(plateletValue, age, gender) : null;

        const plateletCountValue = parseFloat(latest.plateletCount || 0);
        const plateletCountResult = latest.plateletCount ? await PlateletFormula(plateletCountValue, age, gender) : null;

        const mcvValue = parseFloat(latest.mcv || 0);
        const mcvResult = latest.mcv ? await McvFormula(mcvValue, age, gender) : null;

        const mchValue = parseFloat(latest.mch || 0);
        const mchResult = latest.mch ? await MchFormula(mchValue, age, gender) : null;

        const mchcValue = parseFloat(latest.mchc || 0);
        const mchcResult = latest.mchc ? await MchcFormula(mchcValue, age, gender) : null;

        // Differential CBC - simple range checks (no formula files)
        const checkRange = (val, min, max) => {
          if (val < min) return { title: 'ต่ำกว่าปกติ', color: 'red' };
          if (val > max) return { title: 'สูงกว่าปกติ', color: 'red' };
          return { title: 'ปกติ', color: 'green' };
        };

        const neutrophileValue = parseFloat(latest.neutrophile || 0);
        const neutrophileResult = latest.neutrophile ? checkRange(neutrophileValue, 46.5, 75) : null;

        const lymphocyteValue = parseFloat(latest.lymphocyte || 0);
        const lymphocyteResult = latest.lymphocyte ? checkRange(lymphocyteValue, 12, 44) : null;

        const monocyteValue = parseFloat(latest.monocyte || 0);
        const monocyteResult = latest.monocyte ? checkRange(monocyteValue, 0, 11.2) : null;

        const basophilValue = parseFloat(latest.basophil || 0);
        const basophilResult = latest.basophil ? checkRange(basophilValue, 0, 2.5) : null;

        const eosinophilValue = parseFloat(latest.eosinophil || 0);
        const eosinophilResult = latest.eosinophil ? checkRange(eosinophilValue, 0, 9.5) : null;

        setLatestValues({
          rbc: {
            value: latest.rbc ? `${rbcValue.toFixed(2)} x10⁶/µL` : '-',
            status: rbcResult?.title || 'ไม่มีข้อมูล',
            color: rbcResult ? (colorMap[rbcResult.color] || '#34A853') : '#e0e0e0',
            rawValue: rbcValue
          },
          hemoglobin: {
            value: latest.hemoglobin ? `${hemoglobinValue.toFixed(1)} g/dL` : '-',
            status: hemoglobinResult?.title || 'ไม่มีข้อมูล',
            color: hemoglobinResult ? (colorMap[hemoglobinResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: hemoglobinValue
          },
          hematocrit: {
            value: latest.hematocrit ? `${hematocritValue.toFixed(1)} %` : '-',
            status: hematocritResult?.title || 'ไม่มีข้อมูล',
            color: hematocritResult ? (colorMap[hematocritResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: hematocritValue
          },
          wbc: {
            value: latest.wbc ? `${wbcValue.toFixed(1)} x10³/µL` : '-',
            status: wbcResult?.title || 'ไม่มีข้อมูล',
            color: wbcResult ? (colorMap[wbcResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: wbcValue
          },
          platelet: {
            value: latest.platelet ? `${plateletValue.toFixed(0)} x10³/µL` : '-',
            status: plateletResult?.title || 'ไม่มีข้อมูล',
            color: plateletResult ? (colorMap[plateletResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: plateletValue
          },
          plateletCount: {
            value: latest.plateletCount ? `${plateletCountValue.toFixed(0)} x10³/µL` : '-',
            status: plateletCountResult?.title || 'ไม่มีข้อมูล',
            color: plateletCountResult ? (colorMap[plateletCountResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: plateletCountValue
          },
          mcv: {
            value: latest.mcv ? `${mcvValue.toFixed(1)} fL` : '-',
            status: mcvResult?.title || 'ไม่มีข้อมูล',
            color: mcvResult ? (colorMap[mcvResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: mcvValue
          },
          mch: {
            value: latest.mch ? `${mchValue.toFixed(1)} pg` : '-',
            status: mchResult?.title || 'ไม่มีข้อมูล',
            color: mchResult ? (colorMap[mchResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: mchValue
          },
          mchc: {
            value: latest.mchc ? `${mchcValue.toFixed(1)} g/dL` : '-',
            status: mchcResult?.title || 'ไม่มีข้อมูล',
            color: mchcResult ? (colorMap[mchcResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: mchcValue
          },
          neutrophile: {
            value: latest.neutrophile ? `${neutrophileValue.toFixed(1)} %` : '-',
            status: neutrophileResult?.title || 'ไม่มีข้อมูล',
            color: neutrophileResult ? (colorMap[neutrophileResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: neutrophileValue
          },
          lymphocyte: {
            value: latest.lymphocyte ? `${lymphocyteValue.toFixed(1)} %` : '-',
            status: lymphocyteResult?.title || 'ไม่มีข้อมูล',
            color: lymphocyteResult ? (colorMap[lymphocyteResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: lymphocyteValue
          },
          monocyte: {
            value: latest.monocyte ? `${monocyteValue.toFixed(1)} %` : '-',
            status: monocyteResult?.title || 'ไม่มีข้อมูล',
            color: monocyteResult ? (colorMap[monocyteResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: monocyteValue
          },
          basophil: {
            value: latest.basophil ? `${basophilValue.toFixed(1)} %` : '-',
            status: basophilResult?.title || 'ไม่มีข้อมูล',
            color: basophilResult ? (colorMap[basophilResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: basophilValue
          },
          eosinophil: {
            value: latest.eosinophil ? `${eosinophilValue.toFixed(1)} %` : '-',
            status: eosinophilResult?.title || 'ไม่มีข้อมูล',
            color: eosinophilResult ? (colorMap[eosinophilResult.color] || STATUS_COLORS.green) : STATUS_COLORS.gray,
            rawValue: eosinophilValue
          }
        });

        const cbcSummary = await buildCbcStatusSummary({
          rbcItems: rbcArray,
          hemoglobinItems: hemoglobinArray,
          hematocritItems: hematocritArray,
          wbcItems: wbcArray,
          plateletItems: plateletArray,
          plateletCountItems: plateletCountArray,
          mcvItems: mcvArray,
          mchItems: mchArray,
          mchcItems: mchcArray,
          neutrophileItems: neutrophileArray,
          lymphocyteItems: lymphocyteArray,
          monocyteItems: monocyteArray,
          basophilItems: basophilArray,
          eosinophilItems: eosinophilArray,
          age,
          gender,
          formulas: {
            rbc: RbcFormula,
            hb: HemoglobinFormula,
            hct: HematocritFormula,
            wbc: WbcFormula,
            platelet: PlateletFormula,
            mcv: McvFormula,
            mch: MchFormula,
            mchc: MchcFormula,
          },
        });

        if (cbcSummary.latestVisitDate) {
          setLatestDate(formatThaiDate(cbcSummary.latestVisitDate));
        }

        setLatestValues(cbcSummary.latestValues);
        setStatusCardColor(cbcSummary.cardColor);
      }

    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCBCData();
  }, []);

  return (
    <CBCContainer>
      <MainHeader />

      <TopBar>ความสมบูรณ์ของเลือด</TopBar>

      <Header>
        <BackButton onClick={handleBack}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle>ความสมบูรณ์ของเลือด (CBC)</PageTitle>
        {latestDate && <DateText>บันทึกล่าสุดวันที่ {latestDate}</DateText>}
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : allRecords.length === 0 ? (
          <EmptyState>ไม่พบข้อมูลการตรวจ</EmptyState>
        ) : (
          <>
            {/* Status Card */}
            {(() => {
              // Check for any abnormal values among the tracked parameters (Hemoglobin, WBC, Platelets count)
              const abnormalValues = [];
              const hasData = [
                latestValues.hemoglobin,
                latestValues.wbc,
                latestValues.plateletCount,
              ].some(v => v.value !== '-' && v.color !== STATUS_COLORS.gray);

              if (latestValues.hemoglobin.value !== '-' && latestValues.hemoglobin.color !== '#34A853') {
                abnormalValues.push({ name: 'Hemoglobin', ...latestValues.hemoglobin });
              }
              if (latestValues.wbc.value !== '-' && latestValues.wbc.color !== '#34A853') {
                abnormalValues.push({ name: 'WBC', ...latestValues.wbc });
              }
              if (latestValues.plateletCount.value !== '-' && latestValues.plateletCount.color !== '#34A853') {
                abnormalValues.push({ name: 'Platelets count', ...latestValues.plateletCount });
              }

              // Determine card color: gray (no data), green (normal), red (abnormal)
              let cardColor = STATUS_COLORS.gray;
              if (hasData) {
                cardColor = abnormalValues.length > 0 ? STATUS_COLORS.red : STATUS_COLORS.green;
              }

              return (
                <StatusCard $bgColor={statusCardColor}>
                  <CBCIcon src="./ความสมบูรณ์ของเลือดสี.png" alt="ความสมบูรณ์ของเลือด" />
                  <StatusInfo>
                    {!hasData ? (
                      <StatusTitle style={{ color: 'white' }}>ไม่มีข้อมูล</StatusTitle>
                    ) : abnormalValues.length === 0 ? (
                      <StatusTitle style={{ color: 'white' }}>ผลตรวจปกติ</StatusTitle>
                    ) : (
                      <>
                        {abnormalValues.map((item, index) => (
                          <AbnormalItem key={index}>
                            <AbnormalName>{item.name}</AbnormalName>
                            <AbnormalValue>{item.value}</AbnormalValue>
                            <AbnormalStatus>{item.status}</AbnormalStatus>
                          </AbnormalItem>
                        ))}
                      </>
                    )}
                  </StatusInfo>
                </StatusCard>
              );
            })()}

            {/* แสดง 2 การ์ดล่าสุด */}
            {cbcRecords.map((record, index) => (
              <ResultCard key={index}>
                <CardHeader>ผลตรวจ</CardHeader>
                <CardBody>
                  <DateRow>
                    <DateLabel>วันที่ตรวจ</DateLabel>
                    <DateValue>{formatDisplayDate(record.date)}</DateValue>
                  </DateRow>
                  <HospitalRow>
                    <HospitalLabel>โรงพยาบาล</HospitalLabel>
                    <HospitalValue>{record.hospital}</HospitalValue>
                  </HospitalRow>
                  <TestResultsSection>
                    {record.rbc && (
                      <TestResultRow>
                        <TestLabel>RBC (Red Blood Cell)</TestLabel>
                        <TestValue>{record.rbc} x10⁶/µL</TestValue>
                      </TestResultRow>
                    )}
                    {record.hemoglobin && (
                      <TestResultRow>
                        <TestLabel>Hemoglobin</TestLabel>
                        <TestValue>{record.hemoglobin} g/dL</TestValue>
                      </TestResultRow>
                    )}
                    {record.hematocrit && (
                      <TestResultRow>
                        <TestLabel>Hematocrit</TestLabel>
                        <TestValue>{record.hematocrit} %</TestValue>
                      </TestResultRow>
                    )}
                    {record.wbc && (
                      <TestResultRow>
                        <TestLabel>WBC (White Blood Cell)</TestLabel>
                        <TestValue>{record.wbc} x10³/µL</TestValue>
                      </TestResultRow>
                    )}
                    {record.platelet && (
                      <TestResultRow>
                        <TestLabel>Platelet</TestLabel>
                        <TestValue>{record.platelet} x10³/µL</TestValue>
                      </TestResultRow>
                    )}
                    {record.plateletCount && (
                      <TestResultRow>
                        <TestLabel>Platelets count</TestLabel>
                        <TestValue>{record.plateletCount} x10³/µL</TestValue>
                      </TestResultRow>
                    )}
                    {record.mcv && (
                      <TestResultRow>
                        <TestLabel>MCV</TestLabel>
                        <TestValue>{record.mcv} fL</TestValue>
                      </TestResultRow>
                    )}
                    {record.mch && (
                      <TestResultRow>
                        <TestLabel>MCH</TestLabel>
                        <TestValue>{record.mch} pg</TestValue>
                      </TestResultRow>
                    )}
                    {record.mchc && (
                      <TestResultRow>
                        <TestLabel>MCHC</TestLabel>
                        <TestValue>{record.mchc} g/dL</TestValue>
                      </TestResultRow>
                    )}
                    {record.neutrophile && (
                      <TestResultRow>
                        <TestLabel>Neutrophile</TestLabel>
                        <TestValue>{record.neutrophile} %</TestValue>
                      </TestResultRow>
                    )}
                    {record.lymphocyte && (
                      <TestResultRow>
                        <TestLabel>Lymphocyte</TestLabel>
                        <TestValue>{record.lymphocyte} %</TestValue>
                      </TestResultRow>
                    )}
                    {record.monocyte && (
                      <TestResultRow>
                        <TestLabel>Monocyte</TestLabel>
                        <TestValue>{record.monocyte} %</TestValue>
                      </TestResultRow>
                    )}
                    {record.basophil && (
                      <TestResultRow>
                        <TestLabel>Basophil</TestLabel>
                        <TestValue>{record.basophil} %</TestValue>
                      </TestResultRow>
                    )}
                    {record.eosinophil && (
                      <TestResultRow>
                        <TestLabel>Eosinophil</TestLabel>
                        <TestValue>{record.eosinophil} %</TestValue>
                      </TestResultRow>
                    )}
                    {record.rbcMorphology && (
                      <TestResultRow>
                        <TestLabel>RBC morphology</TestLabel>
                        <TestValue>{record.rbcMorphology}</TestValue>
                      </TestResultRow>
                    )}
                    {CBC_EXTRA_DISPLAY_FIELDS.map(({ field, label, unit }) => (
                      record[field] ? (
                        <TestResultRow key={field}>
                          <TestLabel>{label}</TestLabel>
                          <TestValue>{record[field]}{unit}</TestValue>
                        </TestResultRow>
                      ) : null
                    ))}
                  </TestResultsSection>
                </CardBody>
              </ResultCard>
            ))}

            {/* ตารางประวัติทั้งหมด */}
            <HistorySection>
              <HistoryTitle>ประวัติการตรวจทั้งหมด</HistoryTitle>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell>ความเสี่ยง</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {allRecords.map((record, index) => {
                    const values = [];
                    if (record.wbc) values.push(`WBC: ${record.wbc}`);
                    if (record.rbc) values.push(`RBC: ${record.rbc}`);
                    if (record.hemoglobin) values.push(`Hb: ${record.hemoglobin}`);
                    if (record.hematocrit) values.push(`Hct: ${record.hematocrit}`);
                    if (record.platelet) values.push(`PLT: ${record.platelet}`);
                    if (record.plateletCount) values.push(`Platelets count: ${record.plateletCount}`);
                    if (record.mcv) values.push(`MCV: ${record.mcv}`);
                    if (record.mch) values.push(`MCH: ${record.mch}`);
                    if (record.mchc) values.push(`MCHC: ${record.mchc}`);
                    if (record.neutrophile) values.push(`Neutrophile: ${record.neutrophile}%`);
                    if (record.lymphocyte) values.push(`Lymphocyte: ${record.lymphocyte}%`);
                    if (record.monocyte) values.push(`Monocyte: ${record.monocyte}%`);
                    if (record.basophil) values.push(`Basophil: ${record.basophil}%`);
                    if (record.eosinophil) values.push(`Eosinophil: ${record.eosinophil}%`);
                    if (record.rbcMorphology) values.push(`RBC morphology: ${record.rbcMorphology}`);
                    CBC_EXTRA_DISPLAY_FIELDS.forEach(({ field, label, unit }) => {
                      if (record[field]) values.push(`${label}: ${record[field]}${unit}`);
                    });

                    return (
                      <TableRow key={index}>
                        <TableCell>{formatDisplayDate(record.date)}</TableCell>
                        <TableCell>{values.join(', ') || '-'}</TableCell>
                        <TableCell style={{ color: record.riskColor || '#34A853', fontWeight: 'bold' }}>{record.riskStatus || 'ปกติ'}</TableCell>
                        <TableCell>
                          <EditButton onClick={() => handleEditClick(record)}>
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                          </EditButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {/* Edit Modal */}
      {showEditModal && editingRecord && (
        <Modal onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>

            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            <FormGroup>
              <Label>WBC (x10³/µL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.wbc}
                onChange={(e) => setEditForm({ ...editForm, wbc: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>RBC (x10⁶/µL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.rbc}
                onChange={(e) => setEditForm({ ...editForm, rbc: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>RBC morphology</Label>
              <Input
                type="text"
                value={editForm.rbcMorphology}
                onChange={(e) => setEditForm({ ...editForm, rbcMorphology: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Hemoglobin (g/dL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.hemoglobin}
                onChange={(e) => setEditForm({ ...editForm, hemoglobin: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Hematocrit (%)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.hematocrit}
                onChange={(e) => setEditForm({ ...editForm, hematocrit: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Platelet (x10³/µL)</Label>
              <Input
                type="text"
                step="1"
                value={editForm.platelet}
                onChange={(e) => setEditForm({ ...editForm, platelet: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Platelets count (x10³/µL)</Label>
              <Input
                type="text"
                step="1"
                value={editForm.plateletCount}
                onChange={(e) => setEditForm({ ...editForm, plateletCount: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>MCV (fL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.mcv}
                onChange={(e) => setEditForm({ ...editForm, mcv: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>MCH (pg)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.mch}
                onChange={(e) => setEditForm({ ...editForm, mch: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>MCHC (g/dL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.mchc}
                onChange={(e) => setEditForm({ ...editForm, mchc: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Neutrophile (%)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.neutrophile}
                onChange={(e) => setEditForm({ ...editForm, neutrophile: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Lymphocyte (%)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.lymphocyte}
                onChange={(e) => setEditForm({ ...editForm, lymphocyte: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Monocyte (%)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.monocyte}
                onChange={(e) => setEditForm({ ...editForm, monocyte: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Basophil (%)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.basophil}
                onChange={(e) => setEditForm({ ...editForm, basophil: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Eosinophil (%)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.eosinophil}
                onChange={(e) => setEditForm({ ...editForm, eosinophil: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleModalClose}>ยกเลิก</CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {alertState.isOpen && (
        <CustomAlert message={alertState.message} onClose={closeAlert} />
      )}
    </CBCContainer>
  );
};

export default CBCPage;
